/** @jsx jsx */
import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text, jsx, Grid } from 'theme-ui';
import _ from 'lodash';
import Slider from 'react-slick';
import KitCard from '~/components/Kit/Card';
import ProductCard from '~/components/Product/Card';
import Spacer from '../Layout/Spacer';
import useOnScreen from '~/hooks/components/use-on-screen';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import RightChev from '~/assets/images/icons/right-chevron.svg';
import LeftChev from '~/assets/images/icons/left-chevron.svg';
import { PdpButton } from '~/components/Product/PdpButton';

const RelatedProducts = ({ products = [], collectionName }) => {
  const ref = useRef();
  const onScreen = useOnScreen(ref);
  const [closeAll, setCloseAll] = useState(false);

  const settings = {
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: false,
    touchThreshold: 300,
  };

  return (
    <Box
      ref={ref}
      sx={{
        textAlign: 'center',
        marginTop: ['2rem', null, collectionName !== 'custom' ? '8rem' : '0rem'],
        marginBottom: ['1.6rem', null, '6.2rem'],
      }}
    >
      <Text
        sx={{
          color: 'alizarinCrimson',
          fontSize: ['2.4rem', '2.4rem', '4.8rem'],
          fontWeight: 800,
          textTransform: 'uppercase',
          display: 'inline-block',
        }}
      >
        Check out our other products
      </Text>
      <Spacer height="2rem" />
      <Spacer height={['1rem', '1.5rem', '4.1rem']} />
      <Flex
        sx={{
          px: '20px',
          justifyContent: 'center',
          display: ['none', 'block'],
        }}
        onBlur={() => {
          setCloseAll((p) => !p);
        }}
      >
        <Grid
          className={`fade-in-up ${onScreen ? 'visible' : ''}`}
          sx={{
            maxWidth: `${products.length * 315}px`,
            width: '100%',
            margin: '0 auto',
            gridGap: ['35px', '35px', '45px'],
            justifyContent: 'center',
            gridTemplateColumns:
              collectionName === 'hoodies'
                ? ['repeat(1, 285px)', 'repeat(2, 40%)', 'repeat(2, 40%)']
                : ['repeat(2, 1fr)', 'repeat(auto-fill, 25%)', 'repeat(3, 30%)'],
            transitionDelay: '0.7s',
          }}
        >
          {_.map(products, (item) => {
            switch (item.__typename) {
              case 'ContentfulProduct':
                return (
                  <ProductCard
                    key={item.slug}
                    product={item}
                    price={item.price}
                    closeAll={closeAll}
                    sx={{
                      '.product-image': { height: ['70%', '300px', '70%'] },
                      '.product-button': {
                        maxWidth: ['none', '75%', '100%'],
                        width: ['100%'],
                      },
                    }}
                  />
                );

              case 'ContentfulKit':
                return <KitCard key={item.slug} kit={item} />;

              default:
                console.warn(`Oh snap! ${item.__typename} model is not supported yet.`);
                return null;
            }
          })}
        </Grid>
      </Flex>
      <Box
        sx={{
          pb: '5.4rem',
          display: ['block', 'none'],
          mt: 4,
          width: '100%',
          '.slick-list': {
            overflow: 'visible',
          },
        }}
      >
        <Slider
          {...settings}
          nextArrow={
            <SliderButton forwardSx={{ width: '39px', right: '0' }}>
              <RightChev />
            </SliderButton>
          }
          prevArrow={
            <SliderButton forwardSx={{ width: '39px', left: '0' }}>
              <LeftChev />
            </SliderButton>
          }
        >
          {products.map((product, index) => {
            switch (product.__typename) {
              case 'ContentfulProduct':
                return (
                  <ProductCard
                    key={`${product.slug}-related-${index}`}
                    product={product}
                    slug={product.slug}
                    closeAll={closeAll}
                    sx={{
                      padding: '0 10px',
                      '.product-image': {
                        height: '200px',
                      },
                      '.product-button': {
                        maxWidth: ['none', '75%', '100%'],
                      },
                    }}
                  />
                );

              case 'ContentfulKit':
                return <KitCard key={product.slug} kit={product} />;

              default:
                console.warn(`Oh snap! ${product.__typename} model is not supported yet.`);
                return null;
            }
          })}
        </Slider>
      </Box>
      <Spacer height={['1rem', '1.5rem', '4.1rem']} />
      <PdpButton url="/shop" linkText="More Products" />
    </Box>
  );
};

const SliderButton = ({ children, onClick, forwardSx }) => {
  return (
    <button
      sx={{
        top: '57%',
        position: 'absolute',
        transform: 'translateY(-50%)',
        zIndex: 1,
        backgroundColor: 'transparent',
        border: 'none',
        padding: 0,
        ...forwardSx,
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

RelatedProducts.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})),
};

export default RelatedProducts;
